import React, { useState, useEffect } from 'react'
import { Box, Typography, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper } from "@mui/material";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";
import { FaFolder, FaCog, FaCheckSquare, FaExclamationTriangle, FaTimesCircle } from "react-icons/fa";
import { CommonFilterSidebar, CommonSelect } from "../common";
import { primaryBlueColor } from "../common/commonButtonStyles";
import GenericDonutChart from "../Dashboard/GenericDonutChart";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import instance from "../../redux/api";
import { GetWorkflowDashboardSummary, GetAgingAnalysis, GetWorkflowTypeBreakdown, GetWorkflowOverview, GetTeamActivity } from "../../Services/api";
import { toast } from "react-toastify";
import "../common/commonPageStyles.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Fallback static data constants
const FALLBACK_SUMMARY = {
  totalWorkflows: 49,
  inProcess: 15,
  completed: 25,
  overdue: 5,
  rejected: 4
};

const FALLBACK_AGING_ANALYSIS = {
  labels: ["0-5 Days", "6-10 Days", "11-20 Days", "> 20 Days"],
  data: [12, 8, 5, 2]
};

const FALLBACK_WORKFLOW_TYPE_BREAKDOWN = [
  { name: "EMAIL_CLAIM_SUBMISSION", value: 28 },
  { name: "EMAIL_ENDORSEMENT_CHANGE", value: 12 },
  { name: "EMAIL_AMOUNT_RETURN", value: 9 },
];

const FALLBACK_WORKFLOW_OVERVIEW = [
  {
    workflowType: "EMAIL_CLAIM_SUBMISSION",
    inProgress: { completed: 65, remaining: 35 },
    overdue: 3,
    avgAge: "8.2 days",
    status: "On Track"
  },
  {
    workflowType: "EMAIL_ENDORSEMENT_CHANGE",
    inProgress: { completed: 80, remaining: 20 },
    overdue: 1,
    avgAge: "5.7 days",
    status: "On Track"
  },
  {
    workflowType: "EMAIL_AMOUNT_RETURN",
    inProgress: { completed: 45, remaining: 55 },
    overdue: 2,
    avgAge: "12.1 days",
    status: "At Risk"
  }
];

const FALLBACK_TEAM_ACTIVITY = [
  { teamMember: "John Smith", tasksAssigned: 12, tasksOverdue: 2 },
  { teamMember: "Sarah Johnson", tasksAssigned: 15, tasksOverdue: 1 },
  { teamMember: "Mike Davis", tasksAssigned: 8, tasksOverdue: 0 },
  { teamMember: "Emily Chen", tasksAssigned: 10, tasksOverdue: 3 },
  { teamMember: "David Wilson", tasksAssigned: 9, tasksOverdue: 1 }
];

const WorkflowDashboardContainer = () => {
  const toggle = useSelector((state) => state.toggleSideMenu);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);

  // Workflow summary metrics - initialized with fallback data
  const [totalWorkflows, setTotalWorkflows] = useState(FALLBACK_SUMMARY.totalWorkflows);
  const [inProcess, setInProcess] = useState(FALLBACK_SUMMARY.inProcess);
  const [completed, setCompleted] = useState(FALLBACK_SUMMARY.completed);
  const [overdue, setOverdue] = useState(FALLBACK_SUMMARY.overdue);
  const [rejected, setRejected] = useState(FALLBACK_SUMMARY.rejected);

  // Filter state
  const [workflowName, setWorkflowName] = useState("");
  const [dateReceivedFrom, setDateReceivedFrom] = useState("");
  const [dateReceivedTo, setDateReceivedTo] = useState("");
  const [initiatedBy, setInitiatedBy] = useState("");
  const [status, setStatus] = useState("");
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Build query parameters helper
  const buildQueryParams = () => {
    const queryParams = new URLSearchParams();
    
    if (workflowName) {
      queryParams.append('workflow_name', workflowName);
    }
    if (dateReceivedFrom) {
      queryParams.append('date_from', dateReceivedFrom);
    }
    if (dateReceivedTo) {
      queryParams.append('date_to', dateReceivedTo);
    }
    if (initiatedBy) {
      queryParams.append('initiated_by', initiatedBy);
    }
    if (status) {
      queryParams.append('status', status);
    }
    
    return queryParams.toString();
  };

  // Fetch workflow dashboard summary data
  const fetchWorkflowDashboardSummary = async () => {
    try {
      setIsLoading(true);
      
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetWorkflowDashboardSummary}?${queryString}`
        : GetWorkflowDashboardSummary;
      
      const response = await instance.get(url);
      
      if (response.data && !response.isFailed) {
        setTotalWorkflows(response.data.totalWorkflows || FALLBACK_SUMMARY.totalWorkflows);
        setInProcess(response.data.inProcess || FALLBACK_SUMMARY.inProcess);
        setCompleted(response.data.completed || FALLBACK_SUMMARY.completed);
        setOverdue(response.data.overdue || FALLBACK_SUMMARY.overdue);
        setRejected(response.data.rejected || FALLBACK_SUMMARY.rejected);
      } else {
        console.error('Error fetching workflow dashboard summary:', response);
        // Use fallback data on API error
        setTotalWorkflows(FALLBACK_SUMMARY.totalWorkflows);
        setInProcess(FALLBACK_SUMMARY.inProcess);
        setCompleted(FALLBACK_SUMMARY.completed);
        setOverdue(FALLBACK_SUMMARY.overdue);
        setRejected(FALLBACK_SUMMARY.rejected);
      }
    } catch (error) {
      console.error('Error fetching workflow dashboard summary:', error);
      // Use fallback data on API failure
      setTotalWorkflows(FALLBACK_SUMMARY.totalWorkflows);
      setInProcess(FALLBACK_SUMMARY.inProcess);
      setCompleted(FALLBACK_SUMMARY.completed);
      setOverdue(FALLBACK_SUMMARY.overdue);
      setRejected(FALLBACK_SUMMARY.rejected);
      // toast.error('Failed to load workflow dashboard data. Showing fallback data.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch aging analysis data
  const fetchAgingAnalysis = async () => {
    try {
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetAgingAnalysis}?${queryString}`
        : GetAgingAnalysis;
      
      const response = await instance.get(url);
      
      if (response.data && !response.isFailed) {
        setAgingAnalysisData({
          labels: response.data.labels || FALLBACK_AGING_ANALYSIS.labels,
          datasets: [
            {
              label: "Count",
              data: response.data.counts || FALLBACK_AGING_ANALYSIS.data,
              backgroundColor: primaryBlueColor,
              borderColor: primaryBlueColor,
              borderWidth: 1,
            },
          ],
        });
      } else {
        console.error('Error fetching aging analysis:', response);
        // Use fallback data on API error
        setAgingAnalysisData({
          labels: FALLBACK_AGING_ANALYSIS.labels,
          datasets: [
            {
              label: "Count",
              data: FALLBACK_AGING_ANALYSIS.data,
              backgroundColor: primaryBlueColor,
              borderColor: primaryBlueColor,
              borderWidth: 1,
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error fetching aging analysis:', error);
      // Use fallback data on API failure
      setAgingAnalysisData({
        labels: FALLBACK_AGING_ANALYSIS.labels,
        datasets: [
          {
            label: "Count",
            data: FALLBACK_AGING_ANALYSIS.data,
            backgroundColor: primaryBlueColor,
            borderColor: primaryBlueColor,
            borderWidth: 1,
          },
        ],
      });
    }
  };

  // Fetch workflow type breakdown data
  const fetchWorkflowTypeBreakdown = async () => {
    try {
      const queryString = buildQueryParams();
      const url = queryString 
        ? `${GetWorkflowTypeBreakdown}?${queryString}`
        : GetWorkflowTypeBreakdown;
      
      const response = await instance.get(url);
      
      if (response.data && !response.isFailed && Array.isArray(response.data)) {
        setWorkflowTypeBreakdownData(response.data);
      } else {
        console.error('Error fetching workflow type breakdown:', response);
        // Use fallback data on API error
        setWorkflowTypeBreakdownData(FALLBACK_WORKFLOW_TYPE_BREAKDOWN);
      }
    } catch (error) {
      console.error('Error fetching workflow type breakdown:', error);
      // Use fallback data on API failure
      setWorkflowTypeBreakdownData(FALLBACK_WORKFLOW_TYPE_BREAKDOWN);
    }
  };

  // Fetch workflow overview data
  const fetchWorkflowOverview = async () => {
    try {
      const response = await instance.get(GetWorkflowOverview);
      
      if (response.data && !response.isFailed && Array.isArray(response.data)) {
        setWorkflowOverviewData(response.data);
      } else {
        console.error('Error fetching workflow overview:', response);
        // Use fallback data on API error
        setWorkflowOverviewData(FALLBACK_WORKFLOW_OVERVIEW);
      }
    } catch (error) {
      console.error('Error fetching workflow overview:', error);
      // Use fallback data on API failure
      setWorkflowOverviewData(FALLBACK_WORKFLOW_OVERVIEW);
    }
  };

  // Fetch team activity data
  const fetchTeamActivity = async () => {
    try {
      const response = await instance.get(GetTeamActivity);
      
      if (response.data && !response.isFailed && Array.isArray(response.data)) {
        setTeamActivityData(response.data);
      } else {
        console.error('Error fetching team activity:', response);
        // Use fallback data on API error
        setTeamActivityData(FALLBACK_TEAM_ACTIVITY);
      }
    } catch (error) {
      console.error('Error fetching team activity:', error);
      // Use fallback data on API failure
      setTeamActivityData(FALLBACK_TEAM_ACTIVITY);
    }
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchWorkflowDashboardSummary();
    fetchAgingAnalysis();
    fetchWorkflowTypeBreakdown();
    fetchWorkflowOverview();
    fetchTeamActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowName, dateReceivedFrom, dateReceivedTo, initiatedBy, status]);

  // Chart data based on workflow metrics
  const workflowStatusChartData = {
    datasets: [{
      data: [
        { title: "In Process", amount: inProcess, count: inProcess },
        { title: "Completed", amount: completed, count: completed },
        { title: "Rejected / Closed", amount: rejected, count: rejected },
        { title: "Overdue", amount: overdue, count: overdue }
      ]
    }]
  };

  const workflowStatusColors = [
    primaryBlueColor, 
    "rgba(22, 114, 222, 0.7)", 
    "rgba(22, 114, 222, 0.5)", 
    "rgba(22, 114, 222, 0.3)"
  ];

  // Workflow Type Breakdown data state - initialized with fallback data
  const [workflowTypeBreakdownData, setWorkflowTypeBreakdownData] = useState(FALLBACK_WORKFLOW_TYPE_BREAKDOWN);
  
  const maxWorkflowTypeValue = Math.max(...workflowTypeBreakdownData.map(item => item.value), 1);

  // Aging Analysis data state - initialized with fallback data
  const [agingAnalysisData, setAgingAnalysisData] = useState({
    labels: FALLBACK_AGING_ANALYSIS.labels,
    datasets: [
      {
        label: "Count",
        data: FALLBACK_AGING_ANALYSIS.data,
        backgroundColor: primaryBlueColor,
        borderColor: primaryBlueColor,
        borderWidth: 1,
      },
    ],
  });

  const agingAnalysisOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5,
          color: "#333",
          font: {
            size: 11,
          },
        },
        grid: {
          color: "#e0e0e0",
        },
      },
      x: {
        ticks: {
          color: "#333",
          font: {
            size: 11,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Workflow Overview data (Table 1) - initialized with fallback data
  const [workflowOverviewData, setWorkflowOverviewData] = useState(FALLBACK_WORKFLOW_OVERVIEW);

  // Team Activity data - initialized with fallback data
  const [teamActivityData, setTeamActivityData] = useState(FALLBACK_TEAM_ACTIVITY);

  // Pending Workflow Details data (Table 2)
  const pendingWorkflowData = [
    { transactionId: "WF-1024", workflowType: "Workflow Type 1", currentStep: "Approval", age: "12 Days" },
    { transactionId: "WF-1087", workflowType: "Workflow Type 2", currentStep: "Review", age: "18 Days" },
    { transactionId: "WF-1087", workflowType: "Workflow Type 3", currentStep: "Review", age: "15 Days" },
    { transactionId: "WF-1156", workflowType: "Workflow Type 3", currentStep: "Data Entry", age: "25 Days" },
  ];

  // Status options (you can replace with actual options from API)
  const statusOptions = [
    "Pending",
    "In Progress",
    "Completed",
    "Rejected",
    "Cancelled"
  ];

  // Initiated By options (you can replace with actual options from API)
  const initiatedByOptions = [
    // Add actual user names or IDs here
  ];

  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
  };

  const handleApplyFilters = () => {
    // Fetch data with applied filters
    fetchWorkflowDashboardSummary();
    fetchAgingAnalysis();
    fetchWorkflowTypeBreakdown();
  };

  const handleClearFilters = () => {
    // Handle clear filters logic here
    setWorkflowName("");
    setDateReceivedFrom("");
    setDateReceivedTo("");
    setInitiatedBy("");
    setStatus("");
    console.log("Clear filters");
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const isFilterBtnDisable = () => {
    // Return true if filters are disabled, false otherwise
    return !workflowName && !dateReceivedFrom && !dateReceivedTo && !initiatedBy && !status;
  };

  return (
    <div style={Container}>
    
      {/* Filter Sidebar */}
      <CommonFilterSidebar
        isOpen={filterSidebarOpen}
        filterConfigs={[
          {
            type: "textInput",
            label: "Workflow Name",
            placeholder: "Enter workflow name",
            value: workflowName,
            onChange: setWorkflowName,
            onKeyUp: handleEnter,
          },
          {
            type: "dateRange",
            label: "Date Received",
            startDate: dateReceivedFrom,
            setStartDate: setDateReceivedFrom,
            endDate: dateReceivedTo,
            setEndDate: setDateReceivedTo,
            maxDate: new Date(),
            onKeyUp: handleEnter,
          },
          {
            type: "custom",
            content: (
              <>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#000",
                    marginBottom: "12px",
                  }}
                >
                  Initiated By
                </Typography>
                <Box onKeyUp={handleEnter}>
                  <CommonSelect
                    handleChange={(event, value, reason) => {
                      if (reason === "clear") {
                        setInitiatedBy("");
                      } else {
                        setInitiatedBy(value || "");
                      }
                    }}
                    options={initiatedByOptions}
                    placeholder="Select initiated by"
                    value={initiatedBy || ""}
                  />
                </Box>
              </>
            ),
          },
          {
            type: "custom",
            content: (
              <>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#000",
                    marginBottom: "12px",
                  }}
                >
                  Status
                </Typography>
                <Box onKeyUp={handleEnter}>
                  <CommonSelect
                    handleChange={(event, value, reason) => {
                      if (reason === "clear") {
                        setStatus("");
                      } else {
                        setStatus(value || "");
                      }
                    }}
                    options={statusOptions}
                    placeholder="Select status"
                    value={status || ""}
                  />
                </Box>
              </>
            ),
          },
        ]}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        isLoading={false}
        isFilterDisabled={isFilterBtnDisable()}
      />

      {/* Main Content Area */}
      <Box className="main-content-area">
        <Box className="page-header-container">
          <Typography
            variant="h5"
            className="page-heading"
          >
            Workflow Email Monitoring Dashboard
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            
            <IconButton
              onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
              className="filter-toggle-button"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Summary Cards Section - 10% height */}
        <Box
          sx={{
            height: "10%",
            minHeight: "70px",
            maxHeight: "90px",
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Total Workflows Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
            }}
          >
            <FaFolder
              size={24}
              color={primaryBlueColor}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: primaryBlueColor,
                  fontWeight: "600",
                  marginBottom: "2px",
                  lineHeight: 1.2,
                }}
              >
                Total Workflows
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#333",
                  lineHeight: 1.2,
                }}
              >
                {totalWorkflows}
              </Typography>
            </Box>
          </Box>

          {/* In Process Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
            }}
          >
            <FaCog
              size={24}
              color={primaryBlueColor}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: primaryBlueColor,
                  fontWeight: "600",
                  marginBottom: "2px",
                  lineHeight: 1.2,
                }}
              >
                In Process
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#333",
                  lineHeight: 1.2,
                }}
              >
                {inProcess}
              </Typography>
            </Box>
          </Box>

          {/* Completed Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
            }}
          >
            <FaCheckSquare
              size={24}
              color={primaryBlueColor}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: primaryBlueColor,
                  fontWeight: "600",
                  marginBottom: "2px",
                  lineHeight: 1.2,
                }}
              >
                Completed
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#333",
                  lineHeight: 1.2,
                }}
              >
                {completed}
              </Typography>
            </Box>
          </Box>

          {/* Overdue Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
            }}
          >
            <FaExclamationTriangle
              size={24}
              color={primaryBlueColor}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: primaryBlueColor,
                  fontWeight: "600",
                  marginBottom: "2px",
                  lineHeight: 1.2,
                }}
              >
                Overdue
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#333",
                  lineHeight: 1.2,
                }}
              >
                {overdue}
              </Typography>
            </Box>
          </Box>

          {/* Rejected Card */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px 16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
            }}
          >
            <FaTimesCircle
              size={24}
              color={primaryBlueColor}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: primaryBlueColor,
                  fontWeight: "600",
                  marginBottom: "2px",
                  lineHeight: 1.2,
                }}
              >
                Rejected
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#333",
                  lineHeight: 1.2,
                }}
              >
                {rejected}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Charts Section - 25% height */}
        <Box
          sx={{
            height: "35%",
            minHeight: "160px",
            maxHeight: "200px",
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Workflow Status Distribution - Donut Chart */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Workflow Status Distribution
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
                overflow: "hidden",
                padding: "4px 0",
                width: "100%",
                gap: "16px",
              }}
            >
              {/* Legend on the left */}
              <Box 
                sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  minWidth: "120px",
                  maxWidth: "120px",
                  flexShrink: 0,
                }}
              >
                {workflowStatusChartData.datasets[0].data.map((item, index) => (
                  <Box 
                    key={item.title}
                    sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "6px" 
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: "10px", 
                        height: "10px", 
                        backgroundColor: workflowStatusColors[index], 
                        borderRadius: "2px",
                        flexShrink: 0,
                      }} 
                    />
                    <Typography 
                      sx={{ 
                        fontSize: "13px", 
                        color: "#333",
                        whiteSpace: "nowrap",
                        fontWeight: "500",
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Chart on the right */}
              <Box 
                sx={{ 
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 0,
                  overflow: "hidden",
                  width: "120px",
                  flexShrink: 0,
                  position: "relative",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: "120px",
                    height: "120px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <style>
                    {`
                      #workflow-status-distribution {
                        width: 120px !important;
                        max-width: 120px !important;
                        height: 120px !important;
                        max-height: 120px !important;
                        min-height: 120px !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                      }
                      #workflow-status-distribution + div {
                        display: none !important;
                      }
                      #workflow-status-distribution > div {
                        width: 120px !important;
                        height: 120px !important;
                        max-width: 120px !important;
                        max-height: 120px !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                      }
                      #workflow-status-distribution svg,
                      #workflow-status-distribution canvas {
                        width: 120px !important;
                        height: 120px !important;
                        max-width: 120px !important;
                        max-height: 120px !important;
                        box-sizing: border-box !important;
                      }
                      #workflow-status-distribution svg path {
                        stroke: none !important;
                        stroke-width: 0 !important;
                      }
                      #workflow-status-distribution svg circle {
                        stroke: none !important;
                        stroke-width: 0 !important;
                      }
                    `}
                  </style>
                  <GenericDonutChart
                    data={workflowStatusChartData}
                    colorsList={workflowStatusColors}
                    title="workflow-status-distribution"
                    percentageInnerDonut={`${totalWorkflows}`}
                    displayDonutInnerText={true}
                    dataCy="workflow-status-chart"
                    innerRadiusPercent={25}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Aging Analysis Bar Chart */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Aging Analysis
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
                padding: "4px 0",
              }}
            >
              {/* Bar chart using react-chartjs-2 */}
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  minHeight: "120px",
                  position: "relative",
                }}
              >
                <Bar 
                  data={agingAnalysisData} 
                  options={agingAnalysisOptions}
                />
              </Box>
            </Box>
          </Box>

          {/* Workflow Type Breakdown */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Workflow Type Breakdown
            </Typography>
            <Box 
              sx={{ 
                display: "flex", 
                flexDirection: "column", 
                gap: "10px", 
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                minHeight: 0,
                paddingRight: "12px",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#a8a8a8",
                  },
                },
              }}
            >
              {workflowTypeBreakdownData.map((item, index) => {
                const percentage = maxWorkflowTypeValue > 0 ? (item.value / maxWorkflowTypeValue) * 100 : 0;
                return (
                  <Box
                    key={item.name}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "2px",
                      }}
                    >
                      <Typography 
                        sx={{ 
                          fontSize: "12px", 
                          color: "#333", 
                          fontWeight: "500",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: "700", color: "#333", flexShrink: 0, marginLeft: "12px", minWidth: "30px", textAlign: "right" }}>
                        {item.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        height: "20px",
                        backgroundColor: "#e0e0e0",
                        borderRadius: "4px",
                        overflow: "hidden",
                        position: "relative",
                        padding: "2px",
                        boxSizing: "border-box",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${percentage}%`,
                          height: "100%",
                          backgroundColor: primaryBlueColor,
                          borderRadius: "3px",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Tables Section - 65% height, split layout */}
        <Box
          sx={{
            height: "55%",
            minHeight: "400px",
            maxHeight: "600px",
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          {/* Table 1 - Workflow Overview - 50% width, full height */}
          <Box
            sx={{
              width: "50%",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: primaryBlueColor,
                marginBottom: "8px",
              }}
            >
              Workflow Overview
            </Typography>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: "none",
                flex: 1,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "10px",
                },
              }}
            >
              <Table size="small" sx={{ borderCollapse: "separate" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Workflow Type
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      In Progress
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Overdue
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Avg. Age
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workflowOverviewData.map((row, index) => (
                    <TableRow
                      key={row.workflowType}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(22, 114, 222, 0.04)",
                        },
                        borderBottom: index < workflowOverviewData.length - 1 ? "1px solid #e0e0e0" : "none",
                      }}
                    >
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.workflowType}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px 12px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", width: "100px", height: "16px", backgroundColor: "#e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
                          <Box 
                            sx={{ 
                              width: `${row.inProgress.completed}%`, 
                              height: "100%", 
                              backgroundColor: "#ccc",
                            }} 
                          />
                          <Box 
                            sx={{ 
                              width: `${row.inProgress.remaining}%`, 
                              height: "100%", 
                              backgroundColor: "#999",
                            }} 
                          />
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                          fontWeight: "600",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Box sx={{ width: "8px", height: "8px", backgroundColor: "#333", borderRadius: "2px" }} />
                          {row.overdue}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.avgAge}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px 12px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-block",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "600",
                            backgroundColor: row.status === "On Track" ? "rgba(22, 114, 222, 0.1)" : row.status === "At Risk" ? "rgba(22, 114, 222, 0.2)" : "rgba(22, 114, 222, 0.15)",
                            color: "#333",
                          }}
                        >
                          {row.status}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Right Side - 50% width, split vertically */}
          <Box
            sx={{
              width: "50%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {/* Team Activity - Top 50% of right side (25% of container) */}
            <Box
              sx={{
                height: "50%",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                padding: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: primaryBlueColor,
                  marginBottom: "8px",
                }}
              >
                Team Activity
              </Typography>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  boxShadow: "none",
                  flex: 1,
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "10px",
                  },
                }}
              >
                <Table size="small" sx={{ borderCollapse: "separate" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                        }}
                      >
                        Team Member
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                          textAlign: "center",
                        }}
                      >
                        Tasks Assigned
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f5f5f5",
                          color: primaryBlueColor,
                          fontWeight: "600",
                          fontSize: "12px",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${primaryBlueColor}`,
                          textAlign: "center",
                        }}
                      >
                        Tasks Overdue
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamActivityData.map((row, index) => (
                      <TableRow
                        key={row.teamMember}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(22, 114, 222, 0.04)",
                          },
                          borderBottom: index < teamActivityData.length - 1 ? "1px solid #e0e0e0" : "none",
                        }}
                      >
                        <TableCell
                          sx={{
                            color: "#333",
                            fontSize: "12px",
                            padding: "8px 12px",
                          }}
                        >
                          {row.teamMember}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#333",
                            fontSize: "12px",
                            padding: "8px 12px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          {row.tasksAssigned}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#333",
                            fontSize: "12px",
                            padding: "8px 12px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          {row.tasksOverdue}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pending Workflow Details - Bottom 50% of right side (25% of container) */}
            <Box
              sx={{
                height: "50%",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                padding: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: primaryBlueColor,
                  marginBottom: "8px",
                }}
              >
                Pending Workflow Details
              </Typography>
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: "none",
                flex: 1,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "10px",
                },
              }}
            >
              <Table size="small" sx={{ borderCollapse: "separate" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Transaction ID
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Workflow Type
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Current Step
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: primaryBlueColor,
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderBottom: `1px solid ${primaryBlueColor}`,
                      }}
                    >
                      Age
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingWorkflowData.map((row, index) => (
                    <TableRow
                      key={`${row.transactionId}-${index}`}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(22, 114, 222, 0.04)",
                        },
                        borderBottom: index < pendingWorkflowData.length - 1 ? "1px solid #e0e0e0" : "none",
                      }}
                    >
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.transactionId}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.workflowType}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.currentStep}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#333",
                          fontSize: "12px",
                          padding: "8px 12px",
                        }}
                      >
                        {row.age}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default WorkflowDashboardContainer